/***** frontend/src/App.js *****/
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import DeviceManagement from './pages/DeviceManagement';
import AddDevice from './pages/AddDevice';
import EditDevice from './pages/EditDevice';
import PlayerScoring from './pages/PlayerScoring';
import Lobby from './pages/Lobby';
import PartitionDetail from './pages/PartitionDetail';
import DeviceDetail from './pages/DeviceDetail';
import StatisticsPage from './pages/StatisticsPage';
import SanqiDeviceControl from './pages/SanqiDeviceControl';
import SettingsPage from './pages/SettingsPage';
import PartitionManagement from './pages/PartitionManagement';
import RewardPolicy from './pages/RewardPolicy';
import LogManagement from './pages/LogManagement';
import AdminWithdrawalManagement from './pages/AdminWithdrawalManagement';
import AnnouncementManagement from './pages/AnnouncementManagement';
import AdminMonitoring from './pages/AdminMonitoring';
import AdminHome from './pages/AdminHome';
import AdminSanqiErrorManagement from './pages/AdminSanqiErrorManagement';

import Header from './components/Header';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css'; // Tailwind等样式

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div style={{ padding: '20px' }}>加载中...</div>;
  }
  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div style={{ padding: '20px' }}>加载中...</div>;
  }
  if (user && user.role === 'admin') {
    return children;
  }
  return <Navigate to="/login" replace />;
}

export default function App() {
  // 必须把 AuthProvider 放在最外层，useAuth 只能在 AuthProvider 内部用
  // Router needs to be outside of AppInner if AppInner uses useLocation
  return (
    <Router>
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </Router>
  );
}

// 新增：将原 App 内容迁移到 AppInner，保证 useAuth 在 AuthProvider 内部调用
function AppInner() {
  const { socket } = useAuth() || {};
  const [bannerMsg, setBannerMsg] = useState('');
  const [showBanner, setShowBanner] = useState(false);
  const [hasBroadcast, setHasBroadcast] = useState(false);
  const location = useLocation(); // Get current location

  useEffect(() => {
    if (!socket) return;
    const handler = ({ message }) => {
      setBannerMsg(message);
      setShowBanner(true);
      setTimeout(() => setShowBanner(false), 4000);
    };
    socket.on('bannerBroadcast', handler);
    return () => socket.off('bannerBroadcast', handler);
  }, [socket]);

  // 监听广播横幅的显示状态
  useEffect(() => {
    const handleBroadcastUpdate = (event) => {
      // 不再使用这个状态，因为广播条是覆盖的，不影响布局
      // setHasBroadcast(event.detail);
    };

    // 监听广播横幅状态变化
    window.addEventListener('broadcastVisibilityChange', handleBroadcastUpdate);

    return () => {
      window.removeEventListener('broadcastVisibilityChange', handleBroadcastUpdate);
    };
  }, []);

  // Define paths where the header should be hidden
  const pathsWithoutHeader = ['/devices/'];
  const shouldShowHeader = !pathsWithoutHeader.some(path => location.pathname.startsWith(path));

  // 获取当前设备的Header高度，与Header组件中的计算保持一致
  const getHeaderHeight = () => {
    if (window.innerWidth <= 480) return 60;
    if (window.innerWidth <= 768) return 66;
    return 72;
  };

  // 固定的布局：header和页面内容之间没有空白，广播条覆盖在最上层
  const headerHeight = getHeaderHeight();
  const mainContentStyle = {
    marginTop: shouldShowHeader ? `${headerHeight}px` : '0px', // 使用动态计算的header高度
    minHeight: shouldShowHeader ? `calc(100vh - ${headerHeight}px)` : '100vh',
    position: 'relative',
    // 确保没有额外的间距
    padding: 0,
    border: 'none'
    // 移除过渡效果，因为不需要动态调整
  };
  
  if (location.pathname.startsWith('/devices/')) {
    mainContentStyle.background = '#000'; // Set background to black for /devices/ path
  } else {
    mainContentStyle.background = '#fff'; // Default background for other pages
  }

  // 炫酷横幅样式+滚动动画
  return (
    <> {/* Router removed from here */}
      {/* 新的广播横幅组件 */}
      {/* 保留原有的临时横幅（如果需要） */}
      {showBanner && (
        <div style={{
          position: 'fixed',
          top: 60, // 调整位置避免与新广播横幅冲突
          left: 0,
          width: '100vw',
          height: 48,
          background: 'linear-gradient(90deg,#ff9800cc 0%,#ff3c3ccc 50%,#ff9800cc 100%)',
          color: '#fff',
          textAlign: 'center',
          zIndex: 10002, // 确保高于新广播横幅
          fontWeight: 'bold',
          fontSize: 20,
          boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          borderBottom: '2px solid #fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(12px) saturate(180%)', // 玻璃效果
          WebkitBackdropFilter: 'blur(12px) saturate(180%)', // 兼容Safari
          borderRadius: '0 0 18px 18px'
        }}>
          <div
            style={{
              display: 'inline-block',
              whiteSpace: 'nowrap',
              animation: 'banner-marquee 4s linear both',
              fontFamily: 'Consolas, monospace',
              textShadow: '0 0 8px #fff, 0 0 16px #ff0',
              letterSpacing: 1.5,
              paddingLeft: '100vw'
            }}
          >
            {bannerMsg}
          </div>
          <style>
            {`
            @keyframes banner-marquee {
              0%   { transform: translateX(0); }
              100% { transform: translateX(-120vw); }
            }
            `}
          </style>
        </div>
      )}
      {shouldShowHeader && <Header />} {/* Conditionally render Header */}
      <div 
        className="animate__animated animate__fadeIn" 
        style={mainContentStyle} // Use dynamic style for marginTop and background
      >
        <Routes>
          {/* 路由规则 */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/adminlogin" element={<AdminLogin />} />

          {/* 管理端 */}
          <Route
            path="/admin/*"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          >
            <Route index element={<AdminHome />} /          >
            <Route index element={<AdminHome />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="devices" element={<DeviceManagement />} />
            <Route path="devices/add" element={<AddDevice />} />
            <Route path="devices/edit/:id" element={<EditDevice />} />
            <Route path="partitions" element={<PartitionManagement />} />
            <Route path="statistics" element={<StatisticsPage />} />
            <Route path="monitoring" element={<AdminMonitoring />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="reward-policy" element={<RewardPolicy />} />
            <Route path="logs" element={<LogManagement />} />
            <Route path="withdrawals" element={<AdminWithdrawalManagement />} />
            <Route path="announcements" element={<AnnouncementManagement />} />
            <Route path="sanqi-errors" element={<AdminSanqiErrorManagement />} />
          </Route>

          {/* 普通玩家 */}
          <Route
            path="/lobby"
            element={
              <PrivateRoute>
                <Lobby />
              </PrivateRoute>
            }
          />
          <Route
            path="/lobby/partition/:partitionId"
            element={
              <PrivateRoute>
                <PartitionDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/devices/:deviceId"
            element={
              <PrivateRoute>
                <DeviceDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/player/scoring"
            element={
              <PrivateRoute>
                <PlayerScoring />
              </PrivateRoute>
            }
          />
          <Route
            path="/sanqi"
            element={
              <PrivateRoute>
                <SanqiDeviceControl />
              </PrivateRoute>
            }
          />

          {/* 404 */}
          <Route path="*" element={<h2 style={{ padding: '20px' }}>404: 页面未找到</h2>} />
        </Routes>
        <ToastContainer />
      </div>
    </>
  );
}

const styles = {
  banner: {
    position: 'fixed',
    top: 0,
    width: '100%',
    backgroundColor: '#f90',
    color: '#fff',
    textAlign: 'center',
    padding: '10px',
    zIndex: 1000,
    fontWeight: 'bold'
  }
};